import React, { useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { ExpandCollapse } from '@tds/core-expand-collapse';

import { Row } from './css';
import { Box } from '../design/css';
import SelectFilters from './selectFilters';
import TypeAheadFilters from './typeAheadFilters';
import OrderIdFilter from './orderIdFilter';
import DateFilters from './dateFilters';
import FiltersButtons from './filtersButtons';
import { CONSTANT } from '../utils/constant';
import request from '../utils/request';
import {
  ChannelModel,
  DealershipModel,
  TypeAheadModel,
  getOptions
} from '../orders/models';
import { TranslationsContext } from '../../context/translations';

const fetchOutletCodes = () => {
  return request.get('/outletCodes');
};

const fetchSaleSubmitterNames = () => {
  return request.get('/submitterNames');
};

const fetchChannels = () => {
  return request.get('/channels?all=1');
};

const fetchChannelOrgCodes = () => {
  return request.get('/channelOrgCodes');
};

const fetchDealerships = () => {
  return request.get('/dealerships');
};

const initialFilterState = {
  filterStatus: 'All',
  startDate: null,
  endDate: null,
  filterCriteria: 'createdAt',
  channel: '',
  province: [],
  activationLanguage: '',
  orderId: '',
  valueChain: [],
  assignee: [],
  dealership: [],
  channelOrgCode: [],
  outletCode: [],
  salesSubmitterName: []
};

const Filters = ({ history, filterOrders, setFilterPayload, page, users }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [outletCodes, setOutletCodes] = useState(null);
  const [salesSubmitterNames, setSubmitterNames] = useState(null);
  const [channels, setChannels] = useState(null);
  const [channelOrgCodes, setChannelOrgCodes] = useState(null);
  const [listOfDealerships, setDealerships] = useState(null);
  const [isResetFilters, setIsResetFilters] = useState(false);
  const [filterState, setFilterState] = useState(initialFilterState);
  const {
    filterStatus,
    startDate,
    endDate,
    filterCriteria,
    assignee,
    channel,
    province,
    orderId,
    activationLanguage,
    valueChain,
    dealership,
    channelOrgCode,
    outletCode,
    salesSubmitterName
  } = filterState;
  const filterPayload = {
    status: filterStatus === 'All' ? null : filterStatus,
    startDate,
    endDate,
    filterCriteria,
    channel,
    orderId,
    activationLanguage,
    province: province.map(row => row.label),
    valueChain: valueChain.map(row => row.value),
    assigned_user_id: assignee.length && assignee[0].id,
    dealership: dealership.length && dealership[0].label,
    channelOrgCode: channelOrgCode.length && channelOrgCode[0].id,
    outletCode: outletCode.length && outletCode[0].id,
    salesSubmitterName: salesSubmitterName.length && salesSubmitterName[0].id
  };
  const isFilterApplied =
    JSON.stringify(initialFilterState) !== JSON.stringify(filterState);

  useEffect(() => {
    setIsLoading(true);
    Promise.allSettled([
      fetchOutletCodes(),
      fetchSaleSubmitterNames(),
      fetchChannels(),
      fetchChannelOrgCodes(),
      fetchDealerships()
    ]).then(
      ([
        outletCodesResp,
        saleSubmitterNameResp,
        channelsResp,
        channelOrgCodesResp,
        dealershipResp
      ]) => {
        const {
          value: {
            data: { outletCodes: outletCodesList }
          }
        } = outletCodesResp;
        const {
          value: {
            data: { submitterNames }
          }
        } = saleSubmitterNameResp;
        const {
          value: {
            data: { channels: channelsData }
          }
        } = channelsResp;
        const {
          value: {
            data: { channelOrgCodes: channelOrganisationCodes }
          }
        } = channelOrgCodesResp;
        const {
          value: {
            data: { dealerships }
          }
        } = dealershipResp;
        setOutletCodes(getOptions(outletCodesList, TypeAheadModel));
        setChannels(getOptions(channelsData, ChannelModel));
        setChannelOrgCodes(
          getOptions(channelOrganisationCodes, TypeAheadModel)
        );
        setSubmitterNames(getOptions(submitterNames, TypeAheadModel));
        setDealerships(getOptions(dealerships, DealershipModel));
        setIsLoading(false);
      }
    );
  }, []);

  useEffect(() => {
    if (isResetFilters) {
      setFilterPayload(initialFilterState);
      filterOrders();
      setIsResetFilters(false);
    }
  }, [filterState]);

  useEffect(() => {
    if (isFilterApplied) {
      if (isResetFilters) {
        setFilterState(initialFilterState);
      }
    } else if (isResetFilters) {
      setIsResetFilters(false);
    }
  }, [isResetFilters]);

  const profile = request.getProfile();
  const roleId = profile && profile.role.id;
  const {
    ROLES: { ADMIN }
  } = CONSTANT;
  const {
    filters: filterLabels,
    buttons: buttonLabels,
    provinces,
    ORDERS
  } = useContext(TranslationsContext);

  const statusFilters = [
    { text: ORDERS.allOrders, value: 'All' },
    { text: ORDERS.newStatus, value: 'New' },
    { text: ORDERS.inProgress, value: 'In Progress' },
    { text: ORDERS.actionRequired, value: 'Action Required' },
    { text: ORDERS.completed, value: 'Completed' },
    { text: ORDERS.canceled, value: 'Canceled' },
    { text: ORDERS.responded, value: 'Responded' },
    {
      text: ORDERS.waitingOnCustomerResponse,
      value: 'Waiting on Customer Response'
    },
    { text: ORDERS.waitingOnCredit, value: 'Waiting on Credit' },
    { text: ORDERS.waitingOnInternet, value: 'Waiting on Internet' },
    { text: ORDERS.waitingOnNTAOrDSR, value: 'Waiting on NTA or DSR' }
  ];

  const provinceList = [
    { text: provinces.Alberta, value: 'ALBERTA' },
    { text: provinces['British Columbia'], value: 'BRITISH_COLUMBIA' },
    { text: provinces.Saskatchewan, value: 'SASKATCHEWAN' },
    { text: provinces.Ontario, value: 'ONTARIO' },
    { text: provinces.Manitoba, value: 'MANITOBA' },
    { text: provinces.Quebec, value: 'QUEBEC' },
    { text: provinces.Atlantic, value: 'ATLANTIC' }
  ];

  const languageList = [
    { text: 'English', value: 'ENGLISH' },
    { text: 'French', value: 'FRENCH' }
  ];

  const valueChainList = [
    { text: ORDERS.valueChain.TSB, value: 'TSB' },
    { text: ORDERS.valueChain.ADT_BUS, value: 'ADT_BUS' },
    { text: ORDERS.valueChain.ADT_CONS, value: 'ADT_CONS' },
    { text: ORDERS.valueChain.COMPLEX_LEAD, value: 'COMPLEX_LEAD' },
    { text: ORDERS.valueChain.WLN, value: 'WLN' },
    { text: ORDERS.valueChain.TH_OPT_OUT, value: 'TH_OPT_OUT' }
  ];

  const getFilterLabel = name =>
    `${filterLabels.filterBy} ${name.toLowerCase()}:`;

  const getStatuses = () =>
    roleId === ADMIN
      ? statusFilters
      : statusFilters.filter(i => i.value !== 'Responded');

  const updateFilters = (key, value) => {
    setFilterState({ ...filterState, [key]: value });
  };

  const onResetFilters = () => {
    setIsResetFilters(true);
  };

  const onApplyFilters = () => {
    setFilterPayload(filterPayload);
    if (page && page > 1) {
      history.push('/orders');
    } else {
      filterOrders(filterPayload);
    }
  };

  const selectFilters = [
    {
      label: getFilterLabel(filterLabels.status),
      placeholder: filterLabels.status,
      options: getStatuses(),
      value: filterStatus,
      onChange: ({ target: { value } }) => updateFilters('filterStatus', value)
    },
    {
      label: getFilterLabel(filterLabels.channel),
      placeholder: filterLabels.channel,
      options: channels,
      value: channel,
      onChange: ({ target: { value } }) => updateFilters('channel', value)
    },
    {
      id: 'province',
      label: getFilterLabel(filterLabels.province),
      placeholder: filterLabels.province,
      options: provinceList.map(i => ({ label: i.text, value: i.value })),
      value: province,
      onChange: updateFilters,
      isMultiSelect: true
    },
    {
      id: 'ActivationLanguage',
      label: getFilterLabel(filterLabels.activationLanguage),
      placeholder: filterLabels.activationLanguage,
      options: languageList,
      value: activationLanguage,
      onChange: ({ target: { value } }) =>
        updateFilters('activationLanguage', value)
    },
    {
      id: 'valueChain',
      label: getFilterLabel(filterLabels.valueChain),
      placeholder: filterLabels.valueChain,
      options: valueChainList.map(i => ({ label: i.text, value: i.value })),
      value: valueChain,
      onChange: updateFilters,
      isMultiSelect: true
    }
  ];

  const typeAheadFilters = [
    {
      label: getFilterLabel(filterLabels.assignee),
      placeholder: filterLabels.assignee,
      options: users,
      selected: assignee,
      onChange: value => updateFilters('assignee', value)
    },
    {
      label: getFilterLabel(filterLabels.submitter),
      placeholder: filterLabels.submitter,
      options: salesSubmitterNames,
      selected: salesSubmitterName,
      onChange: value => updateFilters('salesSubmitterName', value)
    },
    {
      label: getFilterLabel(filterLabels.dealershipName),
      placeholder: filterLabels.dealershipName,
      options: listOfDealerships,
      selected: dealership,
      onChange: value => updateFilters('dealership', value)
    },
    {
      label: getFilterLabel(filterLabels.channelOrgCode),
      placeholder: filterLabels.channelOrgCode,
      options: channelOrgCodes,
      selected: channelOrgCode,
      onChange: value => updateFilters('channelOrgCode', value)
    },
    {
      label: getFilterLabel(filterLabels.outletCode),
      placeholder: filterLabels.outletCode,
      options: outletCodes,
      selected: outletCode,
      onChange: value => updateFilters('outletCode', value)
    }
  ];

  return (
    <Box pb={48}>
      <ExpandCollapse topDivider={false}>
        <ExpandCollapse.Panel id="filters" header={filterLabels.filters}>
          {isLoading || !users ? (
            <div style={{ height: '32px', padding: '16px 0' }}>
              <p>Filters are loading...</p>
            </div>
          ) : (
            <>
              <Row justifyContent="space-between">
                <SelectFilters filters={selectFilters} />
                <TypeAheadFilters filters={typeAheadFilters} />
                <OrderIdFilter
                  label={getFilterLabel(filterLabels.orderID)}
                  placeholder={filterLabels.orderID}
                  updateFilters={updateFilters}
                  value={orderId}
                />
                <DateFilters
                  filterState={filterState}
                  updateFilters={updateFilters}
                  filterLabels={filterLabels}
                />
              </Row>
              <FiltersButtons
                onApplyFilters={onApplyFilters}
                onResetFilters={onResetFilters}
                buttonLabels={buttonLabels}
              />
            </>
          )}
        </ExpandCollapse.Panel>
      </ExpandCollapse>
    </Box>
  );
};

Filters.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func
  }).isRequired,
  filterOrders: PropTypes.func.isRequired,
  setFilterPayload: PropTypes.func.isRequired,
  page: PropTypes.number,
  users: PropTypes.arrayOf(PropTypes.object)
};

Filters.defaultProps = {
  page: undefined,
  users: null
};

export default Filters;
